import * as React from 'react';
import format from 'date-fns/format'
import 'chartjs-adapter-date-fns';
import { Line } from 'react-chartjs-2';
import {
    Chart as ChartJS,
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
} from 'chart.js';
import _ from 'lodash'
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';

const endpoint = "/tastraveltimes/chart";

export const chartOptions = {
    responsive: true,
    plugins: {
        legend: {
            position: 'top',
        },
        title: {
            display: false
        },
    },
};

const getChartJson = async (endpoint, setJson, queryStr) => {
    const res = await fetch(process.env.REACT_APP_API_HOST + endpoint + "?" + queryStr, {
        method: 'GET',
        headers: {
            'Accept': 'application/json',
            'Content-Type': 'application/json',
        },
    });
    res.json().then(res => {
        setJson(res);
    })
}

const getQueryString = (id) => {
    return new URLSearchParams({ id: id }).toString();
}

const config = {
    data: {
        labels: [],
        datasets: [{
            hidden: false,
            label: 'Typical',
            fill: false,
            backgroundColor: 'rgb(204,0,0,0.7)',
            borderColor: 'rgb(204,0,0,0.7)',
            borderWidth: 3,
            pointRadius: 0,
            data: [],
        },{
            hidden: false,
            label: 'Today',
            fill: false,
            backgroundColor: 'rgb(0,155,74,0.7)',
            borderColor: 'rgb(0,155,74,0.7)',
            borderWidth: 3,
            pointRadius: 0,
            data: [],
        }]
    }
}

const processData = (json, setData) => {
    const mydata = _.cloneDeep(config);
    mydata.data.datasets[1].data = [...json.todTravelTimes];
    mydata.data.datasets[0].data = [...json.avgHistoricalTravelTimes];
    setData(mydata);
}

ChartJS.register(
    TimeScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
);


const TravelTimesChart = (props) => {
    const [data, setData] = React.useState(null);
    const [json, setJson] = React.useState(null);
    const id = props.id;
    const todayMidnight = new Date();
    todayMidnight.setHours(0, 0, 0, 0);

    const todayLast = new Date();
    todayLast.setHours(23, 59, 59, 59);


    React.useEffect(() => {
        if (id) {
            getChartJson(endpoint, setJson, getQueryString(id));
        }
    }, [id])


    React.useEffect(() => {
        if (json) processData(json, setData);
    }, [json])

    const gridColor = "#999999";
    if (data) {

        return (
                <Line height={300}
                datasetIdKey='travelTimesChart'
                data={data.data}
                options={{
                    responsive: true,
                    interaction: {
                        intersect: false,
                        mode: 'nearest',
                    },
                    plugins: {
                        legend: {
                            labels: {
                                filter: item => item.text !== 'none',
                                boxHeight: 0,
                                color: '#000000'
                            },
                            position: 'top',
                            align: 'end'
                        },
                        filler: {
                            propagate: true
                        },
                        tooltip: {
                            titleAlign: 'left',
                            enabled: false,
                            position: 'nearest',
                            external: externalTooltipHandler,
                            callbacks: {
                                title: function(context) {
                                    const unixTS = context[0].parsed.x || '';
                                    if('' !== unixTS) {
                                        return "Travel Time @ " + format(unixTS, 'h:mm a')
                                    }
                                    return "";
                                }
                            }
                        }
                    },
                    scaleShowValues: true,
                    scales: {
                        y: {
                            type: 'linear',
                            title: {
                                display: true,
                                text: 'Travel Times (minutes)',
                                color: '#000000'
                            },
                            ticks: {
                                color: '#000000'
                            },
                            grid: {
                                color: gridColor
                            }
                        },
                        x: {
                            min: todayMidnight.getTime(),
                            max: todayLast.getTime(),
                            type: 'time',
                            ticks: {
                                color: '#000000'
                            },
                            grid: {
                                color: gridColor
                            },
                            time: {
                                unit: 'hour'
                            }
                        }
                    }
                }}
                plugins={[
                    {
                        id: "increase-legend-spacing",
                        beforeInit(chart) {
                            // Get reference to the original fit function
                            const originalFit = (chart.legend).fit;
                            // Override the fit function
                            (chart.legend).fit = function fit() {
                                // Call original function and bind scope in order to use `this` correctly inside it
                                originalFit.bind(chart.legend)();
                                this.height += 10;
                            };
                        }
                    }
                ]}
            />
        )
    } else {
        return (
            <Box sx={{ height: 300, mt: 5, textAlign: "center" }}><Typography variant="h5">Loading Data</Typography></Box>
        )
    }
}

const getOrCreateTooltip = (chart) => {
    let tooltipEl = document.getElementById('chart-tooltip');

    if (!tooltipEl) {
        tooltipEl = document.createElement('div');
        tooltipEl.id = "chart-tooltip"

        tooltipEl.style.background = 'rgba(0, 0, 0, 0.7)';
        tooltipEl.style.borderRadius = '3px';
        tooltipEl.style.color = 'white';
        tooltipEl.style.opacity = 1;
        tooltipEl.style.pointerEvents = 'none';
        tooltipEl.style.position = 'absolute';
        tooltipEl.style.transform = 'translate(-50%, 0)';
        tooltipEl.style.transition = 'all .1s ease';
        tooltipEl.style.minWidth = "200px";
        tooltipEl.style.zIndex = 2000;

        const table = document.createElement('table');
        table.style.margin = '0px';

        tooltipEl.appendChild(table);
        document.getElementById("root").appendChild(tooltipEl);
    }

    return tooltipEl;
};

const externalTooltipHandler = (context) => {
    // Tooltip Element
    const { chart, tooltip } = context;
    const tooltipEl = getOrCreateTooltip(chart);

    // Hide if no tooltip
    if (tooltip.opacity === 0) {
        tooltipEl.style.opacity = 0;
        return;
    }

    // Set Text
    if (tooltip.body) {
        const titleLines = tooltip.title || [];
        var bodyLines = tooltip.body.map(b => b.lines);
        bodyLines = bodyLines.filter(l => {
            return l[0].indexOf("none") === -1 && l[0].indexOf("MPH") === -1
        })

        const tableHead = document.createElement('thead');

        titleLines.forEach(title => {
            const tr = document.createElement('tr');
            tr.style.borderWidth = 0;

            const th = document.createElement('th');
            th.style.borderWidth = 0;
            th.style.textAlign="left";
            const text = document.createTextNode(title);

            th.appendChild(text);
            tr.appendChild(th);
            tableHead.appendChild(tr);
        });

        const tableBody = document.createElement('tbody');
        bodyLines.forEach((body, i) => {
            const colors = tooltip.labelColors[i];

            const span = document.createElement('span');
            span.style.background = colors.backgroundColor;
            span.style.borderColor = colors.borderColor;
            span.style.borderWidth = '2px';
            span.style.marginRight = '10px';
            span.style.height = '10px';
            span.style.width = '10px';
            span.style.display = 'inline-block';

            const tr = document.createElement('tr');
            tr.style.backgroundColor = 'inherit';
            tr.style.borderWidth = 0;

            const td = document.createElement('td');
            td.style.borderWidth = 0;

            const text = document.createTextNode(body + " minutes");

            td.appendChild(span);
            td.appendChild(text);
            tr.appendChild(td);
            tableBody.appendChild(tr);
        });

        const tableRoot = tooltipEl.querySelector('table');

        // Remove old children
        while (tableRoot && tableRoot.firstChild) {
            tableRoot.firstChild.remove();
        }

        // Add new children
        tableRoot.appendChild(tableHead);
        tableRoot.appendChild(tableBody);
    }

    const rect = chart.canvas.getBoundingClientRect();
    // Display, position, and set styles for font
    tooltipEl.style.opacity = 1;
    tooltipEl.style.left = rect.x + tooltip.caretX + 'px';
    tooltipEl.style.top = rect.y + tooltip.caretY + 'px';
    tooltipEl.style.font = tooltip.options.bodyFont.string;
    tooltipEl.style.padding = tooltip.options.padding + 'px ' + tooltip.options.padding + 'px';
};

export default TravelTimesChart;